import { useState } from 'react';

export const defaultRowsPerPageOptions = [30, 50, 100, 200, 500];

export type TOrder = 'asc' | 'desc';

export type TUseTableState = {
  defaultDense?: boolean;
  defaultOrderBy?: string;
  defaultOrder?: TOrder;
  defaultCurrentPage?: number;
  defaultRowsPerPage?: number;
  defaultSelected?: string[];
};

export default function useTable(
  props: TUseTableState
): {
  dense: boolean;
  order: TOrder;
  page: number;
  orderBy: string;
  rowsPerPage: number;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onSelectRow: (id: string) => void;
  onSort: (id: string) => void;
  onSelectAllRows: (checked: boolean, newSelecteds: string[]) => void;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onChangeDense: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
} {
  const [dense, setDense] = useState<boolean>(props?.defaultDense || false);

  const [orderBy, setOrderBy] = useState<string>(
    props?.defaultOrderBy || 'createdAt'
  );

  const [order, setOrder] = useState<TOrder>(props?.defaultOrder || 'desc');

  const [page, setPage] = useState<number>(props?.defaultCurrentPage || 0);

  const [rowsPerPage, setRowsPerPage] = useState<number>(
    props?.defaultRowsPerPage || defaultRowsPerPageOptions[0]
  );

  const [selected, setSelected] = useState<string[]>(
    props?.defaultSelected || []
  );

  const onSort = (id: string) => {
    const isAsc = orderBy === id && order === 'asc';

    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  const onSelectRow = (id: string) => {
    const selectedIndex = selected.indexOf(id);

    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const onSelectAllRows = (checked: boolean, newSelecteds: string[]) => {
    if (checked) {
      setSelected(newSelecteds);
      return;
    }

    setSelected([]);
  };

  const onChangePage = (event, newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onChangeDense = (event) => {
    setDense(event.target.checked);
  };

  return {
    dense,
    order,
    page,
    setPage,
    orderBy,
    rowsPerPage,
    //
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangePage,
    onChangeDense,
    onChangeRowsPerPage,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function descendingComparator(
  // eslint-disable-next-line
  a: any,
  // eslint-disable-next-line
  b: any,
  orderBy: string
): number {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }

  if (b[orderBy] > a[orderBy]) {
    return 1;
  }

  return 0;
}

export function getComparator(
  order: string,
  orderBy: string
): (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  a: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  b: any
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function emptyRows(
  page: number,
  rowsPerPage: number,
  arrayLength: number
): number {
  return page > 0 ? Math.max(0, (1 + page) * rowsPerPage - arrayLength) : 0;
}
